import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../environments/environment';

export interface CompanyOnboardingStartResponse {
    success: boolean;
    code?: string;
    data?: {
        id: string;
        resumeToken: string;
        currentStep: number;
        status: string;
        userId: string;
        email: string;
        created: boolean;
        data?: CompanyOnboardingData;
    };
}

export interface CompanyRepresentativeOnboardingData {
    legalFirstName?: string;
    legalLastName?: string;
    legalFiscalCode?: string;
    legalDocumentNumber?: string;
    legalDocumentFront?: string;
    legalDocumentBack?: string;
    legalFiscalCodeFront?: string;
    legalFiscalCodeBack?: string;
}

export interface CompanyContactsOnboardingData {
    legalAddress?: string;
    legalAddressNumber?: string | number;
    legalZip?: string;
    legalProvince?: string;
    legalCity?: string;
    legalPhone?: string;
}

export interface CompanyInfoOnboardingData {
    name?: string;
    fiscalCode?: string;
    vat?: string;
    sdi?: string;
    pec?: string;
    address?: string;
    addressNumber?: string | number;
    zip?: string;
    province?: string;
    city?: string;
    companyEmail?: string;
    iban?: string;
}

export interface CompanyServicesOnboardingData {
    serviceId?: string;
    billingPeriod?: 'monthly' | 'yearly';
    serviceIds?: string[];
}

export interface CompanyTermsOnboardingData {
    termsAccepted?: boolean;
    privacyAccepted?: boolean;
    clausesAccepted?: boolean;
}

export interface CompanyOnboardingData {
    representative?: CompanyRepresentativeOnboardingData;
    contacts?: CompanyContactsOnboardingData;
    companyInfo?: CompanyInfoOnboardingData;
    services?: CompanyServicesOnboardingData;
    terms?: CompanyTermsOnboardingData;
}

export interface CompanyOnboardingResponse {
    success: boolean;
    code?: string;
    data?: {
        id: string;
        resumeToken: string;
        currentStep: number;
        status: string;
        data: CompanyOnboardingData;
    };
}

export interface OnboardingApiResult {
    success: boolean;
    code?: string;
    message?: string;
}

export interface OnboardingErrorCopy {
    title: string;
    text: string;
}

const ONBOARDING_ERROR_COPY: Record<string, { title: string; text: string }> = {
    ONBOARDING_NOT_FOUND: {
        title: 'Sessione non trovata',
        text: 'La registrazione non è più disponibile o il collegamento non è valido. Torna al passo Account e riprova con email e password.'
    },
    ONBOARDING_PASSWORD_MISMATCH: {
        title: 'Onboarding già in corso',
        text: 'Esiste già una registrazione in corso con questa email, ma la password inserita è diversa.'
    },
    INVALID_ONBOARDING_CREDENTIALS: {
        title: 'Dati non validi',
        text: 'Email o password non rispettano i requisiti. Controlla i dati e riprova.'
    },
    INVALID_ONBOARDING_STEP: {
        title: 'Passo non valido',
        text: 'Non è possibile salvare questo passo della registrazione. Aggiorna la pagina e riprova.'
    },
    ONBOARDING_START_ERROR: {
        title: 'Impossibile avviare la registrazione',
        text: 'Non è stato possibile creare l\'account. Riprova tra qualche istante.'
    },
    ONBOARDING_SAVE_ERROR: {
        title: 'Salvataggio non riuscito',
        text: 'Non è stato possibile salvare i dati. Riprova tra qualche istante.'
    },
    ONBOARDING_GET_ERROR: {
        title: 'Ripristino non riuscito',
        text: 'Non è stato possibile recuperare i dati salvati. Aggiorna la pagina e riprova.'
    },
    USER_ALREADY_EXIST: {
        title: 'Email già registrata',
        text: 'Esiste già un account con questa email. Accedi oppure usa un indirizzo diverso.'
    },
    COMPANY_ALREADY_EXIST: {
        title: 'Società già registrata',
        text: 'Esiste già una società con questa partita IVA o codice fiscale.'
    },
    USER_NOT_FOUND: {
        title: 'Utente non trovato',
        text: 'Non esiste un account con questi dati. Controlla email e codice OTP.'
    },
    USER_PENDING_OTP: {
        title: 'Codice OTP da confermare',
        text: 'Devi ancora confermare il codice inviato via email prima di continuare.'
    },
    OTP_NOT_VALID: {
        title: 'Codice OTP non valido',
        text: 'Il codice inserito non è corretto. Controlla l\'email e riprova.'
    },
    SIGNUP_ERROR: {
        title: 'Registrazione non riuscita',
        text: 'Non è stato possibile creare la società. Controlla i dati e riprova.'
    },
    INCORRECT_PASSWORD: {
        title: 'Password errata',
        text: 'La password inserita non è corretta.'
    },
    PASSWORD_REQUIRED: {
        title: 'Password mancante',
        text: 'Per completare la registrazione devi reinserire la password nel passo Account manager.'
    },
    UNKNOWN_ERROR: {
        title: 'Errore imprevisto',
        text: 'Si è verificato un errore imprevisto. Riprova tra qualche istante.'
    },
    COMPANY_NOT_FOUND: {
        title: 'Società non trovata',
        text: 'Non è stato possibile trovare la società associata a questa registrazione.'
    },
    NO_COMPANY_FOUND: {
        title: 'Società non trovata',
        text: 'Non è stato possibile trovare la società associata a questa registrazione.'
    },
    STRIPE_SUBSCRIPTION_NOT_CREATED: {
        title: 'Pagamento non avviato',
        text: 'Non è stato possibile creare l\'abbonamento. Riprova tra qualche istante.'
    }
};

function toOnboardingResult(response: any, fallbackCode: string): OnboardingApiResult {
    if (response?.success === true) {
        return {success: true};
    }
    const code = response?.code || response?.error?.code || fallbackCode;
    return {
        success: false,
        code,
        message: response?.message || response?.error?.message
    };
}

export function getOnboardingErrorCopy(response: any, fallbackText?: string): OnboardingErrorCopy {
    const code = String(response?.code || response?.error?.code || '').trim();
    const mapped = code ? ONBOARDING_ERROR_COPY[code] : undefined;
    const genericText = 'Non è stato possibile completare l\'operazione. Riprova tra qualche istante.';
    return {
        title: mapped?.title ?? 'Si è verificato un errore',
        text: mapped?.text ?? fallbackText ?? genericText
    };
}

@Injectable({providedIn: 'root'})
export class OnboardingService {

    constructor(private http: HttpClient) {
    }

    private getPublicRequestHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': environment.publicApiToken,
        });
    }

    async startCompanyManager(email: string, password: string): Promise<CompanyOnboardingStartResponse> {
        try {
            return await this.http.post<CompanyOnboardingStartResponse>(
                environment.authApiUrl + '/onboarding',
                {email: email.trim().toLowerCase(), password},
                {headers: this.getPublicRequestHeaders()}
            ).toPromise() as CompanyOnboardingStartResponse;
        } catch (error: any) {
            return error?.error ?? {success: false, code: 'ONBOARDING_START_ERROR'};
        }
    }

    async saveStep(resumeToken: string, currentStep: number): Promise<OnboardingApiResult> {
        if (!resumeToken) return {success: false, code: 'ONBOARDING_NOT_FOUND'};

        try {
            const response: any = await this.http.patch(
                environment.authApiUrl + `/onboarding/${encodeURIComponent(resumeToken)}/step`,
                {currentStep},
                {headers: this.getPublicRequestHeaders()}
            ).toPromise();
            return toOnboardingResult(response, 'ONBOARDING_SAVE_ERROR');
        } catch (error: any) {
            console.error('Unable to save onboarding step:', error);
            return toOnboardingResult(error?.error ?? error, 'ONBOARDING_SAVE_ERROR');
        }
    }

    async getOnboarding(resumeToken: string): Promise<CompanyOnboardingResponse | null> {
        if (!resumeToken) return null;

        try {
            return await this.http.get<CompanyOnboardingResponse>(
                environment.authApiUrl + `/onboarding/${encodeURIComponent(resumeToken)}`,
                {headers: this.getPublicRequestHeaders()}
            ).toPromise() as CompanyOnboardingResponse;
        } catch (error) {
            console.error('Unable to restore onboarding:', error);
            return null;
        }
    }

    async saveRepresentative(
        resumeToken: string,
        representative: CompanyRepresentativeOnboardingData
    ): Promise<OnboardingApiResult> {
        if (!resumeToken) return {success: false, code: 'ONBOARDING_NOT_FOUND'};

        try {
            const response: any = await this.http.patch(
                environment.authApiUrl + `/onboarding/${encodeURIComponent(resumeToken)}/representative`,
                representative,
                {headers: this.getPublicRequestHeaders()}
            ).toPromise();
            return toOnboardingResult(response, 'ONBOARDING_SAVE_ERROR');
        } catch (error: any) {
            console.error('Unable to save representative onboarding data:', error);
            return toOnboardingResult(error?.error ?? error, 'ONBOARDING_SAVE_ERROR');
        }
    }

    async saveContacts(
        resumeToken: string,
        contacts: CompanyContactsOnboardingData
    ): Promise<OnboardingApiResult> {
        if (!resumeToken) return {success: false, code: 'ONBOARDING_NOT_FOUND'};

        try {
            const response: any = await this.http.patch(
                environment.authApiUrl + `/onboarding/${encodeURIComponent(resumeToken)}/contacts`,
                contacts,
                {headers: this.getPublicRequestHeaders()}
            ).toPromise();
            return toOnboardingResult(response, 'ONBOARDING_SAVE_ERROR');
        } catch (error: any) {
            console.error('Unable to save onboarding contact data:', error);
            return toOnboardingResult(error?.error ?? error, 'ONBOARDING_SAVE_ERROR');
        }
    }

    async saveCompanyInfo(
        resumeToken: string,
        companyInfo: CompanyInfoOnboardingData
    ): Promise<OnboardingApiResult> {
        if (!resumeToken) return {success: false, code: 'ONBOARDING_NOT_FOUND'};

        try {
            const response: any = await this.http.patch(
                environment.authApiUrl + `/onboarding/${encodeURIComponent(resumeToken)}/company`,
                companyInfo,
                {headers: this.getPublicRequestHeaders()}
            ).toPromise();
            return toOnboardingResult(response, 'ONBOARDING_SAVE_ERROR');
        } catch (error: any) {
            console.error('Unable to save onboarding company data:', error);
            return toOnboardingResult(error?.error ?? error, 'ONBOARDING_SAVE_ERROR');
        }
    }

    async saveServices(
        resumeToken: string,
        services: CompanyServicesOnboardingData
    ): Promise<OnboardingApiResult> {
        if (!resumeToken) return {success: false, code: 'ONBOARDING_NOT_FOUND'};

        try {
            const response: any = await this.http.patch(
                environment.authApiUrl + `/onboarding/${encodeURIComponent(resumeToken)}/services`,
                services,
                {headers: this.getPublicRequestHeaders()}
            ).toPromise();
            return toOnboardingResult(response, 'ONBOARDING_SAVE_ERROR');
        } catch (error: any) {
            console.error('Unable to save onboarding services data:', error);
            return toOnboardingResult(error?.error ?? error, 'ONBOARDING_SAVE_ERROR');
        }
    }

    async saveTerms(
        resumeToken: string,
        terms: CompanyTermsOnboardingData
    ): Promise<OnboardingApiResult> {
        if (!resumeToken) return {success: false, code: 'ONBOARDING_NOT_FOUND'};

        try {
            const response: any = await this.http.patch(
                environment.authApiUrl + `/onboarding/${encodeURIComponent(resumeToken)}/terms`,
                terms,
                {headers: this.getPublicRequestHeaders()}
            ).toPromise();
            return toOnboardingResult(response, 'ONBOARDING_SAVE_ERROR');
        } catch (error: any) {
            console.error('Unable to save onboarding terms:', error);
            return toOnboardingResult(error?.error ?? error, 'ONBOARDING_SAVE_ERROR');
        }
    }
}
